import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../app/authStore';
import { api, ApiError, describeError } from '../lib/api';
import { Button } from './ui';
import { Notice } from './form';

/**
 * 账号/组织状态横幅。
 *
 * 两件事只在「不满足时」出现：邮箱未验证、企业未通过审核。
 * 用横幅而不是弹窗：它们不阻断使用，只解释哪些按钮为什么不可用。
 */
export function VerificationBanner() {
  const session = useAuthStore((state) => state.session);
  const refresh = useAuthStore((state) => state.refresh);

  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [devUrl, setDevUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setMessage(null);
    setDevUrl(null);
    setError(null);
  }, [session?.tenant.id, session?.user.emailVerified]);

  if (!session) return null;

  const emailPending = !session.user.emailVerified;
  const tenantPending =
    session.tenant.kind === 'enterprise' && session.tenant.verificationStatus === 'pending';
  const tenantRejected =
    session.tenant.kind === 'enterprise' && session.tenant.verificationStatus === 'rejected';

  if (!emailPending && !tenantPending && !tenantRejected) return null;

  async function resend() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{ sent: boolean; devVerificationUrl?: string }>(
        '/api/auth/verify-email/resend',
      );
      if (result.sent) {
        setMessage('验证邮件已重新发送，请检查收件箱。');
        setDevUrl(result.devVerificationUrl ?? null);
      } else {
        setMessage('邮箱已验证。');
        await refresh();
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : describeError(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mb-5 space-y-2">
      {emailPending ? (
        <Notice
          tone="warn"
          action={
            <Button size="sm" variant="secondary" loading={busy} onClick={resend}>
              重新发送
            </Button>
          }
        >
          邮箱 <span className="font-medium">{session.user.email}</span> 尚未验证：
          验证后才能下单、邀请成员与生成 API Key。
          {message ? <span className="ml-1">{message}</span> : null}
          {error ? <span className="ml-1 text-danger">{error}</span> : null}
          {devUrl ? (
            <a href={devUrl} className="ml-1 break-all text-brand hover:underline">
              开发环境验证链接
            </a>
          ) : null}
        </Notice>
      ) : null}

      {tenantPending ? (
        <Notice tone="info">
          企业「{session.tenant.name}」的资质正在审核中：邀请成员、生成 API Key 与申请发票暂不可用，
          下单与付款不受影响。
          <Link to="/org/verification" className="ml-1 text-brand hover:underline">
            查看审核资料
          </Link>
        </Notice>
      ) : null}

      {tenantRejected ? (
        <Notice tone="danger">
          企业资质审核未通过，请补充资料后重新提交。
          <Link to="/org/verification" className="ml-1 text-brand hover:underline">
            去重新提交
          </Link>
        </Notice>
      ) : null}
    </div>
  );
}
